import { Link } from "@inertiajs/react"
import type { Video } from "@/types"
import { OptimizedImage } from "@/components/ui/optimized-image"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16",
  landscape: "16:9",
  square: "1:1",
}

const WIZARD_STEPS: Record<string, { label: string; index: number }> = {
  style: { label: "Style", index: 0 },
  script: { label: "Script", index: 1 },
  audio: { label: "Audio", index: 2 },
  storyboard: { label: "Storyboard", index: 3 },
  branding: { label: "Branding", index: 4 },
  review: { label: "Review", index: 5 },
}

const STATUS_LABELS: Record<Video["status"], string> = {
  draft: "In progress",
  scripted: "Scripted",
  rendered: "Rendered",
  published: "Published",
  archived: "Archived",
}

const ASPECT_CLASSES = {
  portrait: { small: "h-14 w-8", medium: "h-20 w-[45px]" },
  landscape: { small: "h-8 w-14", medium: "h-[45px] w-20" },
  square: { small: "size-11", medium: "size-16" },
} as const

export default function VideoThumbnail({
  video,
  size = "small",
}: {
  video: Video
  size?: "small" | "medium"
}) {
  const isDraft = video.status === "draft"
  const step = WIZARD_STEPS[video.wizard_step] || { label: video.wizard_step, index: 0 }
  const progress = ((step.index + 1) / 6) * 100

  return (
    <Link
      href={`/dashboard/videos/${video.id}`}
      className="group/thumb relative shrink-0"
      title={`${video.name} — ${STATUS_LABELS[video.status]}`}
    >
      <div
        className={`overflow-hidden rounded-md shadow-sm ring-1 ring-charcoal/10 transition-all duration-150 group-hover/thumb:scale-105 group-hover/thumb:shadow-md ${ASPECT_CLASSES[video.aspect_ratio][size]}`}
      >
        {video.hero_image_url ? (
          <OptimizedImage
            src={video.hero_image_url}
            alt={video.name}
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-gradient-to-br from-charcoal to-slate">
            <svg
              className={`text-white/30 ${size === "small" ? "size-3" : "size-4"}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
              />
            </svg>
          </div>
        )}

        {/* Play icon overlay */}
        <div className="absolute inset-0 flex items-center justify-center rounded-md opacity-0 transition-opacity duration-150 group-hover/thumb:opacity-100">
          <div
            className={`flex items-center justify-center rounded-full bg-white/90 shadow ${size === "small" ? "size-5" : "size-7"}`}
          >
            <svg
              className={`text-charcoal ${size === "small" ? "ml-px size-2.5" : "ml-0.5 size-3"}`}
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5.14v14l11-7-11-7z" />
            </svg>
          </div>
        </div>

        {/* Format badge */}
        <div className="absolute bottom-0.5 right-0.5">
          <span className="rounded bg-black/50 px-1 py-px text-[7px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
            {FORMAT_LABELS[video.aspect_ratio]}
          </span>
        </div>

        {/* Draft progress bar */}
        {isDraft && (
          <div className="absolute inset-x-0 bottom-0">
            <div className="h-0.5 bg-black/20">
              <div
                className="h-full bg-red/80"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </Link>
  )
}
